import { createClient } from '@supabase/supabase-js';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseKey) {
  console.error("Supabase URL and Key are missing. Check your .env file.");
}

export const supabase = createClient(supabaseUrl, supabaseKey);

const getAuthorizedTenantIds = () => {
  try {
    const raw = localStorage.getItem('authorizedRestaurantIds');
    const parsed = JSON.parse(raw || '[]');
    return Array.isArray(parsed) ? parsed.map(String) : [];
  } catch {
    return [];
  }
};

const assertTenantAccess = (tenantId, tableName) => {
  const allowedTenantIds = getAuthorizedTenantIds();
  if (!tenantId) return;
  if (allowedTenantIds.length === 0) return;
  if (!allowedTenantIds.includes(String(tenantId))) {
    throw new Error(`Tenant guard blocked access to ${tableName}. Active restaurant is not in authorized memberships.`);
  }
};

/**
 * SaaS Step 2.4: Query Helper
 * Automatically injects the current restaurant_id into queries.
 * Usage: saas('orders').select('*') or saas('orders').insert({ ... })
 */
export const saas = (tableName) => {
  const tenantId = localStorage.getItem('currentRestaurantId');
  assertTenantAccess(tenantId, tableName);
  const query = supabase.from(tableName);

  if (!tenantId) {
    console.warn(`SaaS Helper: No restaurant_id found in localStorage for table ${tableName}. Query might fail RLS.`);
    return query;
  }

  // --- 1. Auto-inject restaurant_id for INSERTS & UPSERTS ---
  const originalInsert = query.insert.bind(query);
  query.insert = (values, options) => {
    const data = Array.isArray(values)
      ? values.map(v => ({ ...v, restaurant_id: tenantId }))
      : { ...values, restaurant_id: tenantId };
    return originalInsert(data, options);
  };

  const originalUpsert = query.upsert.bind(query);
  query.upsert = (values, options) => {
    const data = Array.isArray(values)
      ? values.map(v => ({ ...v, restaurant_id: tenantId }))
      : { ...values, restaurant_id: tenantId };
    return originalUpsert(data, options);
  };

  // --- 2. Auto-filter for SELECT, UPDATE, DELETE ---
  // We wrap the select/update/delete methods to automatically append .eq()
  const originalSelect = query.select.bind(query);
  query.select = (...args) => originalSelect(...args).eq('restaurant_id', tenantId);

  const originalUpdate = query.update.bind(query);
  query.update = (...args) => originalUpdate(...args).eq('restaurant_id', tenantId);

  const originalDelete = query.delete.bind(query);
  query.delete = (...args) => originalDelete(...args).eq('restaurant_id', tenantId);

  return query;
};

/**
 * SaaS Step 2.5: RPC Helper
 * Automatically injects p_restaurant_id into RPC calls.
 */
saas.rpc = (functionName, params = {}) => {
  const tenantId = localStorage.getItem('currentRestaurantId');
  if (!tenantId) {
    throw new Error(`Active restaurant context is missing for RPC ${functionName}. Select a restaurant and try again.`);
  }
  return supabase.rpc(functionName, { ...params, p_restaurant_id: tenantId });
};
